import React, { ReactElement } from "react";
import Flickity, { FlickityOptions } from "react-flickity-component";
import "flickity/dist/flickity.min.css";
import { styled } from "@sourceful/shared-components";
import toRem from "@styles/postprocess/conversion/to-rem";

const StyledImage = styled("img", {
  objectFit: "cover",
  borderRadius: "$medium",
  margin: toRem(15),
  variants: {
    size: {
      small: {
        width: toRem(250),
        height: toRem(250),
      },
      normal: {
        width: toRem(350),
        height: toRem(350),
      },
    },
    hasBoxShadow: {
      true: {
        boxShadow: "$lift",
      },
    },
  },
});

const GalleryWrapper = styled("div", {
  ".flickity-button": {
    background: "white",
    boxShadow: "$jump",
  },
  ".previous": {
    marginLeft: "clamp(10px, 5vw, 100px)",
  },
  ".next": {
    marginRight: "clamp(10px, 5vw, 100px)",
  },
  ".flickity-button:focus": {
    $$focusBorder: "$colors$palette1_colour1_tint20",
    boxShadow: "0 0 0 4px $$focusBorder",
  },
  ".flickity-button-icon": {
    margin: "0 auto",
    padding: 3,
    transform: "scale(0.9)",
  },
});

export interface ImageGalleryItem {
  id: string;
  src: string;
  alt: string;
}

export interface ImageGalleryProps {
  renderImage?: (image: ImageGalleryItem) => ReactElement;
  images: ImageGalleryItem[];
  hasBoxShadow?: boolean;
  flicketyOptions?: FlickityOptions;
}

const ImageGallery = ({
  images,
  renderImage,
  hasBoxShadow,
  flicketyOptions,
}: ImageGalleryProps) => {
  return (
    <GalleryWrapper>
      <Flickity
        reloadOnUpdate
        options={{
          initialIndex: Math.floor(images.length / 2),
          wrapAround: true,
          pageDots: false,
          arrowShape: {
            x0: 10,
            x1: 55,
            y1: 45,
            x2: 60,
            y2: 40,
            x3: 20,
          },
          imagesLoaded: true,
          ...flicketyOptions,
        }}
      >
        {images.map(image => {
          if (renderImage) {
            return renderImage(image);
          }

          return (
            <StyledImage
              src={image.src}
              key={image.id}
              alt={image.alt}
              hasBoxShadow={hasBoxShadow}
              size={{
                "@initial": "small",
                "@xs": "normal",
              }}
            />
          );
        })}
      </Flickity>
    </GalleryWrapper>
  );
};

export default ImageGallery;
